import * as z from 'zod';

import {
	embroideryConfigurationFromFormData,
	embroideryConfigurationSchema,
	filterOptionEmbroideriesByBrandIds,
	getConfiguredEmbroidery,
	getDefaultEmbroideryConfiguration,
	getItemsForEmbroideryConfiguration,
} from '@/modules/commerce/embroidery-collection/configuration';
import {
	embroideryTextConfigurationFromFormData,
	embroideryTextConfigurationSchema,
	getConfiguredEmbroideryText,
	getDefaultEmbroideryTextConfiguration,
	getItemsForEmbroideryTextConfiguration,
} from '@/modules/commerce/embroidery-text/configuration';
import type {ItemForPriceFragment} from '@/modules/commerce/item/query';
import type {ProductAdapter} from '@/modules/commerce/product/adapter';
import {
	getBaseCabTableProductBrandsById,
	getFiltersMatchingCabTableProduct,
} from '@/modules/commerce/product/cab-table/filtering';
import type {
	CabTableProductCoreFragment,
	CabTableProductFragment,
} from '@/modules/commerce/product/cab-table/query';
import {DOCUMENT_TYPE_CAB_TABLE_PRODDUCT} from '@/modules/commerce/product/cab-table/schema/names';
import type {ProductDescription} from '@/modules/commerce/product/description';
import {resolveProductImageSettings} from '@/modules/commerce/product/product-image-settings';
import {type ProductImageSide} from '@/modules/commerce/product/ProductImage';
import type {DynamicTextOverlay, Overlay} from '@/modules/commerce/product/ProductImageStack';
import {
	getDefaultOverlayPlacement,
	getOverlayPlacement,
	type ProductImageStackDescription,
} from '@/modules/commerce/product/ProductImageStack';
import type {UnionProductCoreFragment} from '@/modules/commerce/product/query';
import {
	getConfiguredMaterialAndColor,
	getDefaultProductMaterialConfiguration,
	getItemsForProductMaterialConfiguration,
	productMaterialConfigurationFromFormData,
	productMaterialConfigurationSchema,
} from '@/modules/commerce/product-material/configuration';
import {imageFromSanityImage} from '@/modules/foundation/sanity/image/query';

export const powerOutletConfigurationSchema = z.record(z.string(), z.string().nullable());

export type PowerOutletConfig = z.infer<typeof powerOutletConfigurationSchema>;

export const cabTableConfigurationSchema = z.object({
	baseMaterial: productMaterialConfigurationSchema.optional(),
	edgeMaterial: productMaterialConfigurationSchema.optional(),
	embroidery: embroideryConfigurationSchema.optional(),
	embroideryText: embroideryTextConfigurationSchema.optional(),
	cupHolders: z.number().optional(),
	powerOutlets: powerOutletConfigurationSchema.optional(),
	ledLighting: z.boolean().optional(),
	mountingBracket: z.boolean().optional(),
	paperClip: z.boolean().optional(),
});

export type CabTableConfiguration = z.infer<typeof cabTableConfigurationSchema>;

export function parseCabTableConfiguration(value: unknown): CabTableConfiguration | null {
	const result = cabTableConfigurationSchema.safeParse(value);

	if (!result.success) {
		console.warn('Failed to parse cab table product configuration', result.error);
		return null;
	}

	return result.data;
}

export function isCabTableProduct<P extends UnionProductCoreFragment>(
	product: P,
): product is Extract<P, {_type: typeof DOCUMENT_TYPE_CAB_TABLE_PRODDUCT}> {
	return product._type === DOCUMENT_TYPE_CAB_TABLE_PRODDUCT;
}

export function getFilteredEmbroideryOptionForCabTable(product: CabTableProductCoreFragment) {
	if (!product.settings?.embroidery) {
		return null;
	}

	// Filter embroideries to only those matching the product's brands:
	const allowedBrandIds = Array.from(getBaseCabTableProductBrandsById(product).keys());
	return filterOptionEmbroideriesByBrandIds(product.settings.embroidery, allowedBrandIds);
}

/**
 * Returns the simplest (but still valid) configuration of the product.
 */
export function getDefaultCabTableConfiguration(
	product: CabTableProductCoreFragment,
): CabTableConfiguration {
	const defaultConfig: CabTableConfiguration = {};

	if (!product.settings) {
		return defaultConfig;
	}

	const baseMaterial = getDefaultProductMaterialConfiguration(
		product.settings.baseMaterials || [],
	);
	if (baseMaterial) {
		defaultConfig.baseMaterial = baseMaterial;
	}

	const edgeMaterial = getDefaultProductMaterialConfiguration(
		product.settings.edgeMaterials || [],
	);
	if (edgeMaterial) {
		defaultConfig.edgeMaterial = edgeMaterial;
	}

	const embroidery = getFilteredEmbroideryOptionForCabTable(product);
	if (embroidery?.available) {
		defaultConfig.embroidery = getDefaultEmbroideryConfiguration(embroidery);
	}

	if (product.settings.embroideryText?.available) {
		defaultConfig.embroideryText = getDefaultEmbroideryTextConfiguration(
			product.settings.embroideryText,
		);
	}

	if (product.settings?.cupHolder?.available) {
		defaultConfig.cupHolders = 1;
	}

	return defaultConfig;
}

export function cabTableConfigurationFromFormData(
	product: CabTableProductCoreFragment,
	formData: FormData,
): CabTableConfiguration {
	const config: CabTableConfiguration = {};

	const baseMaterial = productMaterialConfigurationFromFormData(
		product.settings?.baseMaterials || [],
		'baseMaterial',
		formData,
	);
	if (baseMaterial) {
		config.baseMaterial = baseMaterial;
	}

	const edgeMaterial = productMaterialConfigurationFromFormData(
		product.settings?.edgeMaterials || [],
		'edgeMaterial',
		formData,
	);
	if (edgeMaterial) {
		config.edgeMaterial = edgeMaterial;
	}

	const embroidery = getFilteredEmbroideryOptionForCabTable(product);
	if (embroidery?.available) {
		config.embroidery = embroideryConfigurationFromFormData(embroidery, 'embroidery', formData);
	}

	if (product.settings?.embroideryText?.available) {
		config.embroideryText = embroideryTextConfigurationFromFormData(
			product.settings.embroideryText,
			'embroideryText',
			formData,
		);
	}

	if (product.settings?.cupHolder?.available) {
		const cupHolders = formData.get('cupHolders');
		if (typeof cupHolders === 'string') {
			const cupHoldersNumber = parseInt(cupHolders, 10);
			if (!isNaN(cupHoldersNumber) && cupHoldersNumber > 0) {
				config.cupHolders = cupHoldersNumber;
			}
		}
	}

	if (product.settings?.powerOutlet?.available) {
		const powerOutlets: PowerOutletConfig = {};
		const maxQuantity = product.settings.powerOutlet?.maxQuantity || 0;

		for (let index = 0; index < maxQuantity; index++) {
			const outletValue = formData.get(`powerOutlets.${index}`);
			if (typeof outletValue === 'string') {
				if (outletValue === 'none') {
					powerOutlets[index] = null;
				} else {
					powerOutlets[index] = outletValue;
				}
			}
		}

		config.powerOutlets = powerOutlets;
	}

	if (product.settings?.ledLighting?.available) {
		const ledLighting = formData.get('ledLighting');
		if (ledLighting === 'true') {
			config.ledLighting = true;
		}
	}

	if (product.settings?.mountingBracket?.available) {
		const mountingBracket = formData.get('mountingBracket');
		if (mountingBracket === 'true') {
			config.mountingBracket = true;
		}
	}

	if (product.settings?.paperClip?.available) {
		const paperClip = formData.get('paperClip');
		if (paperClip === 'true') {
			config.paperClip = true;
		}
	}

	return config;
}

export function getItemsForCabTableConfiguration(
	product: CabTableProductCoreFragment,
	configuration: CabTableConfiguration,
): ItemForPriceFragment[] {
	const items: ItemForPriceFragment[] = [];

	// Add base product items:
	items.push(...(product.settings?.items ?? []));

	// Add configured option items:
	const baseMaterials = product.settings?.baseMaterials;
	if (baseMaterials && configuration.baseMaterial) {
		const baseMaterialItems = getItemsForProductMaterialConfiguration(
			baseMaterials,
			configuration.baseMaterial,
		);
		items.push(...baseMaterialItems);
	}

	const edgeMaterials = product.settings?.edgeMaterials;
	if (edgeMaterials && configuration.edgeMaterial) {
		const edgeMaterialItems = getItemsForProductMaterialConfiguration(
			edgeMaterials,
			configuration.edgeMaterial,
		);
		items.push(...edgeMaterialItems);
	}

	const embroidery = getFilteredEmbroideryOptionForCabTable(product);
	if (embroidery?.available && configuration.embroidery) {
		const embroideryItems = getItemsForEmbroideryConfiguration(
			embroidery,
			configuration.embroidery,
		);
		items.push(...embroideryItems);
	}

	const embroideryText = product.settings?.embroideryText;
	if (embroideryText?.available && configuration.embroideryText) {
		const embroideryTextItems = getItemsForEmbroideryTextConfiguration(
			embroideryText,
			configuration.embroideryText,
		);
		items.push(...embroideryTextItems);
	}

	const cupHolder = product.settings?.cupHolder;
	if (cupHolder?.available && cupHolder.items?.length && configuration.cupHolders) {
		for (let i = 0; i < configuration.cupHolders; i++) {
			items.push(...cupHolder.items);
		}
	}

	const powerOutlet = product.settings?.powerOutlet;
	if (powerOutlet?.available && powerOutlet.types?.length && configuration.powerOutlets) {
		const outletConfig = configuration.powerOutlets;
		const maxQuantity = powerOutlet?.maxQuantity || 0;

		for (let index = 0; index < maxQuantity; index++) {
			const configuredOutletKey = outletConfig[index];

			if (configuredOutletKey) {
				const outletType = powerOutlet.types.find(
					(type) => type._key === configuredOutletKey,
				);

				if (outletType?.items?.length) {
					items.push(...outletType.items);
				}
			}
		}
	}

	const ledLighting = product.settings?.ledLighting;
	if (ledLighting?.available && ledLighting.items?.length && configuration.ledLighting) {
		items.push(...ledLighting.items);
	}

	const mountingBracket = product.settings?.mountingBracket;
	if (
		mountingBracket?.available &&
		mountingBracket.items?.length &&
		configuration.mountingBracket
	) {
		items.push(...mountingBracket.items);
	}

	const paperClip = product.settings?.paperClip;
	if (paperClip?.available && paperClip?.items?.length && configuration.paperClip) {
		items.push(...paperClip.items);
	}

	return items;
}

export type CabTableConfigurationValidationResult = {
	isValid: boolean;
};

export function validateCabTableConfiguration(
	_product: CabTableProductCoreFragment,
	_configuration: CabTableConfiguration,
): CabTableConfigurationValidationResult {
	// FIXME: Implement!

	return {isValid: true};
}

export function isValidCabTableConfiguration(
	product: CabTableProductCoreFragment,
	configuration: CabTableConfiguration,
) {
	const validation = validateCabTableConfiguration(product, configuration);
	return validation.isValid;
}

export function getImageStackForCabTableConfiguration<Side extends ProductImageSide>(
	side: Side,
	product: CabTableProductCoreFragment,
	configuration: CabTableConfiguration | null,
): ProductImageStackDescription | null {
	if (side !== 'top') {
		// For cab shoe trays, we only have a top image:
		return null;
	}

	if (!product.settings) {
		return null;
	}

	// Fetch the most specific (enabled) product image settings:
	const imageSettings = resolveProductImageSettings(product)?.imageSettings;

	if (!imageSettings) {
		return null;
	}

	const overlays: Overlay[] = [];

	// Add base image, if configured:
	let baseImage = imageFromSanityImage(imageSettings.baseImage);
	if (configuration?.baseMaterial) {
		const {configuredMaterial, configuredColor} = getConfiguredMaterialAndColor(
			product.settings?.baseMaterials || [],
			configuration.baseMaterial,
		);

		for (const materialImage of imageSettings.baseImagesForMaterialColor || []) {
			if (
				materialImage.color?._ref &&
				materialImage.material?._ref &&
				materialImage.color._ref === configuredColor?.color?._id &&
				materialImage.material._ref === configuredMaterial?.material?._id
			) {
				const baseMaterialImage = imageFromSanityImage(materialImage.image);
				if (baseMaterialImage) {
					baseImage = baseMaterialImage;
				}
			}
		}
	}

	if (!baseImage) {
		// If there's no base image, we don't need to consider the overlays either:
		return null;
	}

	// Add edge image, if configured:
	let edgeImage = imageFromSanityImage(imageSettings.edgeImage);
	if (configuration?.edgeMaterial) {
		const {configuredMaterial, configuredColor} = getConfiguredMaterialAndColor(
			product.settings?.edgeMaterials || [],
			configuration.edgeMaterial,
		);

		for (const materialImage of imageSettings.edgeImagesForMaterialColor || []) {
			if (
				materialImage.color?._ref &&
				materialImage.material?._ref &&
				materialImage.color._ref === configuredColor?.color?._id &&
				materialImage.material._ref === configuredMaterial?.material?._id
			) {
				const overlayImage = imageFromSanityImage(materialImage.image);
				if (overlayImage) {
					edgeImage = overlayImage;
				}
			}
		}
	}

	if (edgeImage) {
		overlays.push({
			_type: 'imageOverlay',
			image: edgeImage,
			placement: getDefaultOverlayPlacement(),
		});
	}

	// Add cup holder overlays, if configured:
	const cupHolder = product.settings?.cupHolder;
	const cupHolderPlacements = imageSettings.cupHolderImagePlacements;
	if (
		cupHolder?.available &&
		cupHolder.image &&
		cupHolderPlacements &&
		configuration?.cupHolders
	) {
		for (let i = 0; i < configuration.cupHolders; i++) {
			const cupHolderOverlayImage = imageFromSanityImage(cupHolder.image);
			const placement = cupHolderPlacements.at(i);

			if (placement?.isVisible && cupHolderOverlayImage) {
				overlays.push({
					_type: 'imageOverlay',
					image: cupHolderOverlayImage,
					placement: getOverlayPlacement(placement),
				});
			}
		}
	}

	// Add power outlet overlays, if configured:
	const powerOutlet = product.settings?.powerOutlet;
	const powerOutletPlacements = imageSettings.powerOutletImagePlacements;
	if (
		powerOutlet?.available &&
		powerOutlet.types &&
		powerOutletPlacements &&
		configuration?.powerOutlets
	) {
		const outletConfig = configuration.powerOutlets;
		const maxQuantity = powerOutlet?.maxQuantity || 0;

		for (let index = 0; index < maxQuantity; index++) {
			const configuredOutletKey = outletConfig[index];
			const placement = powerOutletPlacements.at(index);

			if (configuredOutletKey && placement?.isVisible) {
				const outletType = powerOutlet.types.find(
					(type) => type._key === configuredOutletKey,
				);

				if (outletType?.image) {
					const overlayImage = imageFromSanityImage(outletType.image);
					if (overlayImage) {
						overlays.push({
							_type: 'imageOverlay',
							image: overlayImage,
							placement: getOverlayPlacement(placement),
						});
					}
				}
			}
		}
	}

	// Add embroidery overlay, if configured:
	const embroidery = getFilteredEmbroideryOptionForCabTable(product);
	if (embroidery?.available && configuration?.embroidery) {
		const placement = imageSettings.embroideryImagePlacement;

		const {configuredEmbroidery} = getConfiguredEmbroidery(
			embroidery,
			configuration.embroidery,
		);

		if (configuredEmbroidery?.image) {
			const overlayImage = imageFromSanityImage(configuredEmbroidery.image);
			if (overlayImage) {
				overlays.push({
					_type: 'imageOverlay',
					image: overlayImage,
					placement: getOverlayPlacement(placement),
				});
			}
		}
	}

	// Add text embroidery overlay, if configured:
	const embroideryText = product.settings?.embroideryText;
	if (embroideryText?.available && configuration?.embroideryText) {
		const placement = imageSettings.embroideryTextPlacement;

		const {configuredFont, configuredColor} = getConfiguredEmbroideryText(
			embroideryText,
			configuration.embroideryText,
		);

		if (configuredFont) {
			const {file} = configuredFont;
			if (file && file.url) {
				const fontId = configuredFont.name ?? file._id;
				overlays.push({
					_type: 'dynamicTextOverlay',
					font: fontId,
					fontUrl: file.url,
					fontSize: embroideryText.fontSize ?? 10,
					lineHeight: embroideryText.lineHeight ?? 1.5,
					origin: embroideryText.origin ?? undefined,
					colorHex: configuredColor?.hex ?? '#ffffff',
					placement: getOverlayPlacement(placement),
					text:
						configuration.embroideryText.text == null
							? (embroideryText?.defaultText ?? '')
							: configuration.embroideryText.text,
				} satisfies DynamicTextOverlay);
			}
		}
	}

	// Add paperclip overlay, if configured:
	const paperClip = product.settings?.paperClip;
	const paperClipPlacement = imageSettings.paperClipImagePlacement;
	if (paperClip?.available && paperClip.image && paperClipPlacement && configuration?.paperClip) {
		const paperClipOverlayImage = imageFromSanityImage(paperClip.image);

		if (paperClipOverlayImage) {
			overlays.push({
				_type: 'imageOverlay',
				image: paperClipOverlayImage,
				placement: getOverlayPlacement(paperClipPlacement),
			});
		}
	}

	return {
		image: baseImage,
		altText: null,
		overlays,
	};
}

export function getProductNameForCabTableConfiguration(
	product: CabTableProductCoreFragment,
	_configuration: CabTableConfiguration,
) {
	return `${product.name}`;
}

export function getDescriptionForCabTableConfiguration(
	product: CabTableProductCoreFragment,
	configuration: CabTableConfiguration,
): ProductDescription {
	const desc: ProductDescription = [];

	const baseMaterials = product.settings?.baseMaterials;

	// NOTE: Outputting description in "production order" (i.e. the order that the options are
	//       applied to the product) as requested by Traktorutstyr.

	if (baseMaterials && configuration.baseMaterial) {
		const {configuredMaterial, configuredColor} = getConfiguredMaterialAndColor(
			baseMaterials,
			configuration.baseMaterial,
		);

		if (configuredMaterial && configuredColor) {
			desc.push({
				title: 'Platemateriale',
				value: [
					{
						title:
							'Farge ' + '(' + (configuredColor.color?.hex ?? 'Uten hekskode') + ')',
						value: configuredColor.color?.name ?? 'Farge uten navn',
					},
					{
						title: 'Materiale',
						value: configuredMaterial.material?.name ?? 'Materiale uten navn',
					},
				],
			});
		}
	}

	const embroidery = getFilteredEmbroideryOptionForCabTable(product);
	if (embroidery?.available && configuration.embroidery) {
		const {configuredCollection, configuredEmbroidery} = getConfiguredEmbroidery(
			embroidery,
			configuration.embroidery,
		);

		if (configuredCollection && configuredEmbroidery) {
			desc.push({
				title: 'Broderi',
				value: [
					{
						title: 'Broderisamling',
						value: configuredCollection.name ?? 'Samling uten navn',
					},
					{
						title: 'Navn',
						value: configuredEmbroidery.name ?? 'Broderi uten navn',
					},
				],
			});
		}
	}

	const embroideryText = product.settings?.embroideryText;
	if (embroideryText?.available && configuration.embroideryText) {
		const {configuredFont, configuredColor} = getConfiguredEmbroideryText(
			embroideryText,
			configuration.embroideryText,
		);

		if (configuredFont) {
			desc.push({
				title: 'Brodert tekst',
				value: [
					{
						title: 'Font',
						value: configuredFont?.name ?? 'Font uten navn',
					},
					{
						title: 'Farge',
						value: configuredColor
							? (configuredColor.name ?? configuredColor.hex ?? 'Farge uten navn')
							: 'Standardfarge',
					},
					{
						title: 'Tekst',
						value: configuration.embroideryText.text ?? '',
					},
				],
			});
		}
	}

	const edgeMaterials = product.settings?.edgeMaterials;
	if (edgeMaterials && configuration.edgeMaterial) {
		const {configuredMaterial, configuredColor} = getConfiguredMaterialAndColor(
			edgeMaterials,
			configuration.edgeMaterial,
		);

		if (configuredMaterial && configuredColor) {
			desc.push({
				title: 'Kantmateriale',
				value: [
					{
						title:
							'Farge ' + '(' + (configuredColor.color?.hex ?? 'Uten hekskode') + ')',
						value: configuredColor.color?.name ?? 'Farge uten navn',
					},
					{
						title: 'Materiale',
						value: configuredMaterial.material?.name ?? 'Materiale uten navn',
					},
				],
			});
		}
	}

	const cupHolder = product.settings?.cupHolder;

	if (cupHolder?.available && configuration.cupHolders) {
		desc.push({title: 'Koppholder', value: `${configuration.cupHolders} stk`});
	}

	const powerOutlet = product.settings?.powerOutlet;

	if (powerOutlet?.available && configuration.powerOutlets) {
		const outletConfig = configuration.powerOutlets;
		const maxQuantity = powerOutlet.maxQuantity || 0;

		for (let index = 0; index < maxQuantity; index++) {
			const configuredOutletKey = outletConfig[index];

			if (configuredOutletKey) {
				const outletType = powerOutlet.types?.find(
					(type) => type._key === configuredOutletKey,
				);

				if (outletType) {
					desc.push({
						title: `Strømuttak ${index + 1}`,
						value: outletType.name ?? 'Strømuttak uten navn',
					});
				}
			}
		}
	}

	const paperClip = product.settings?.paperClip;
	const ledLighting = product.settings?.ledLighting;
	const mountingBracket = product.settings?.mountingBracket;

	const options: string[] = [];

	if (paperClip?.available && configuration.paperClip) {
		options.push('Papirklemme');
	}

	if (ledLighting?.available && configuration.ledLighting) {
		options.push('LED-belysning');
	}

	if (mountingBracket?.available && configuration.mountingBracket) {
		options.push('Monteringsbrakett');
	}

	if (options.length > 0) {
		desc.push({title: 'Andre tilvalg', value: options.join(' - ')});
	}

	return desc;
}

export const cabTableProductAdapter: ProductAdapter<
	CabTableProductFragment,
	CabTableProductCoreFragment
> = {
	getName: getProductNameForCabTableConfiguration,
	getItems: getItemsForCabTableConfiguration,
	getImages: getImageStackForCabTableConfiguration,
	getDescription: getDescriptionForCabTableConfiguration,
	getMatchingFilters: getFiltersMatchingCabTableProduct,
	isValidConfig: isValidCabTableConfiguration,
	getDefaultConfig: getDefaultCabTableConfiguration,
	parseConfigFromFormData: cabTableConfigurationFromFormData,
	parseConfigFromJSON: parseCabTableConfiguration,
};
